import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { ErrorNote } from '../../components/ui/misc';
import { ApiError } from '../../lib/api/errors';
import { useAuth } from './useAuth';
import { AuthLayout } from './LoginPage';

interface MfaState {
  mfaChallengeToken?: string;
  from?: string;
  email?: string;
}

export function MfaChallengePage() {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();
  const state = (useLocation().state as MfaState | null) ?? {};

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!state.mfaChallengeToken) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyMfa(state.mfaChallengeToken as string, code.trim());
      navigate(state.from ?? '/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'That code was not accepted. It may have expired — try the current one from your authenticator.'
          : 'Could not verify the code. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <div className="mb-2 flex justify-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-positive-soft text-positive">
          <Icon name="shield" className="text-[22px]" />
        </div>
      </div>
      <h1 className="text-center text-xl font-semibold text-ink">Two-step verification</h1>
      <p className="mt-1 text-center text-sm text-ink-muted">
        Enter the 6-digit code from your authenticator app{state.email && ' for '}
        {state.email && <span className="font-mono text-xs font-medium text-ink">{state.email}</span>}.
      </p>

      {state.email && (
        <div className="mt-4 flex items-center justify-between rounded border border-line bg-surface-subtle px-3 py-2 text-xs">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-mono text-[10px] font-bold text-primary-fg">
              {initials(state.email)}
            </div>
            <span className="truncate font-mono text-[11px] text-ink-muted">{state.email}</span>
          </div>
          <span className="flex shrink-0 items-center gap-1 rounded border border-positive/20 bg-positive-soft px-2 py-0.5 font-mono text-[10px] font-semibold text-positive">
            <Icon name="check_circle" className="text-[13px]" />
            PASSWORD OK
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Field
          label="Verification code"
          autoComplete="one-time-code"
          // A 6-digit numeric TOTP code AND a `xxxx-xxxx-xxxx` hex recovery
          // code share this one field (`MfaVerifyDto.code`, `@MaxLength(32)`
          // — `mfa.service.ts`'s `tryConsumeRecoveryCode` disambiguates by
          // shape). `inputMode="numeric"`/a digits-only `pattern` would
          // have blocked the recovery-code path outright — found while
          // adding the hint text below that documents that format.
          maxLength={32}
          required
          hint="A 6-digit authenticator code, or a backup recovery code (xxxx-xxxx-xxxx)."
          className="text-center font-mono text-lg tracking-[0.15em]"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button type="submit" loading={busy} className="w-full">
          <span>Verify</span>
          <Icon name="arrow_forward" className="text-[18px]" />
        </Button>
      </form>

      <div className="mt-4 border-t border-sunken pt-4 text-center">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink">
          <Icon name="arrow_back" className="text-[15px]" />
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}

function initials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  const chars = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local.slice(0, 2)];
  return chars.join('').slice(0, 2).toUpperCase();
}
